import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {selectLang, selectNumberFormat, Translate} from "../../../../app/store/reducers/main/mainReducer";
import {useDispatch, useSelector} from "react-redux";
import AdvanceTableWrapper from "../../../common/advance-table/AdvanceTableWrapper";
import {Card, Col, Form, Row} from "react-bootstrap";
import AdvanceTable from "../../../common/advance-table/AdvanceTable";
import AdvanceTableCustomPagination2 from "../../item/datatable/AdvanceTableCustomPagination2";
import numeral from "numeral";
import SoftBadge from "../../../common/SoftBadge";
import classNames from "classnames";
import {transliterate} from "transliteration";
import {debounce} from "underscore";
import ContractorPeriodReportModal from "./ContractorPeriodReportModal";
import {selectNationalCurrency} from "../../../../app/store/reducers/currency/currencyReducer";
import DataTableSettings from "../../DataTableSettings";
import IconButton from "../../../common/IconButton";
import {faGear} from "@fortawesome/free-solid-svg-icons";
import {
    changeContractorPeriodSummaryDataTableSettings,
    loadContractorPeriodSummaryDataTableSettings, selectContractorPeriodSummaryDataTableSettings
} from "../../../../app/store/reducers/report/reportReducer";

export const dependencies = {
    sales_net_price: "sales_count",
    payments_net_price: "payments_count",
    payouts_net_price: "payouts_count",
    refunds_net_price: "refunds_count",
    returns_net_price: "returns_count",
    purchases_net_price: "purchases_count",
    shortage_price: "shortage_count",
    telegram_order_price: "telegram_order_count",
};

const ContractorsPeriodReportSummaryDataTable = ({contractorReports}) => {
    const dispatch = useDispatch();

    const t = Translate;
    const lang = useSelector(selectLang);
    const numberFormat = useSelector(selectNumberFormat);
    const nationalCurrency = useSelector(selectNationalCurrency);
    const activeColumns = useSelector(selectContractorPeriodSummaryDataTableSettings)
    const [filter, setFilter] = useState({
        name: ""
    });
    const [pagination, setPagination] = useState({
        pageSize: 10,
        pageIndex: 1
    });

    const [show, setShow] = useState(false)

    useEffect(() => {
        dispatch(loadContractorPeriodSummaryDataTableSettings())
    }, [])

    const filteredColumns = useMemo(() => {
        return Object.keys(activeColumns)
            .filter(key => !key.includes('count') && !['index', 'name'].includes(key))
            .reduce((obj, key) => {
                if (key in activeColumns) {
                    obj[key] = activeColumns[key];
                }
                return obj;
            }, {});
    }, [activeColumns])

    const getActiveColumns = (activeColumns, columns = []) => {
        return columns?.filter(column => activeColumns[column?.accessor])
    }

    const columns = useMemo(() => {
        return [
            {
                accessor: 'index',
                Header: "#",
                id: "row",
                cellProps: {
                    className: "p-0 text-center",
                    style: {
                        width: 40,
                        minWidth: 40
                    },
                },
                headerProps: {
                    className: "p-0 text-center",
                    style: {
                        width: 40,
                        minWidth: 40
                    }
                },
                Cell: ({row}) => {
                    return `${row.index + 1}`;
                }
            },
            {
                accessor: 'name',
                Header: t(lang, "warehouse.operation.contractor_report.name"),
                Cell: ({row: {original}}) => {
                    return <ContractorPeriodReportModal contractorReport={original}/>;
                }
            },
            {
                accessor: 'sales_profit_by_purchase_price',
                Header: t(lang, "warehouse.operation.report.sales_profit_by_purchase_price"),
                headerProps: {
                    className: "text-center",
                    style: {
                        width: '200px',
                        minWidth: '200px'
                    }
                },
                cellProps: {
                    className: "mx-auto",
                    style: {
                        width: '200px',
                        minWidth: '200px'
                    }
                },
                Cell: ({row: {original}}) => {
                    if (!original.contractor_period_report.sales_profit_by_purchase_price.length) {
                        return (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <SoftBadge bg='primary'
                                           key={nationalCurrency?.id}
                                           className={classNames("w-100")}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(0)}
                                        <small className={'ms-2 fw-bold text-primary'}>{nationalCurrency?.name}</small>
                                    </h6>
                                </SoftBadge>
                            </div>
                        )
                    }

                    return (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            {original.contractor_period_report.sales_profit_by_purchase_price.map((netPrice, index) => (
                                <SoftBadge bg='success'
                                           key={netPrice.currency.id}
                                           className={classNames("w-100", {"mt-2": index !== 0})}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(netPrice.amount)}
                                        <small className={'ms-2 fw-bold text-success'}>{netPrice.currency.name}</small>
                                    </h6>
                                </SoftBadge>
                            ))}
                        </div>
                    )
                }
            },
            {
                accessor: 'sales_count',
                Header: t(lang, "warehouse.operation.report.sales"),
                headerProps: {
                    className: "text-center",
                    colSpan: 2,
                    style: {
                        width: '200px',
                    }
                },
                cellProps: {
                    className: "text-center fw-bold text-dark",
                    style: {
                        width: '60px',
                        minWidth: '60px'
                    }
                },
                Cell: ({row: {original}}) => {
                    return (
                        <h6 className="text-end">{original.contractor_period_report.sales_count}</h6>
                    )
                }
            },
            {
                accessor: 'sales_net_price',
                headerProps: {
                    className: "d-none"
                },
                cellProps: {
                    className: "text-center",
                    style: {
                        minWidth: '140px',
                    }
                },
                Cell: ({row: {original}}) => {
                    if (!original.contractor_period_report.sales_net_price.length) {
                        return (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <SoftBadge bg='primary'
                                           key={nationalCurrency?.id}
                                           className={classNames("w-100")}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(0)}
                                        <small className={'ms-2 fw-bold text-primary'}>{nationalCurrency?.name}</small>
                                    </h6>
                                </SoftBadge>
                            </div>
                        )
                    }

                    return (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            {original.contractor_period_report.sales_net_price.map((netPrice, index) => (
                                <SoftBadge bg='success'
                                           key={netPrice.currency.id}
                                           className={classNames("w-100", {"mt-2": index !== 0})}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(netPrice.amount)}
                                        <small className={'ms-2 fw-bold text-success'}>{netPrice.currency.name}</small>
                                    </h6>
                                </SoftBadge>
                            ))}
                        </div>
                    )
                }
            },
            {
                accessor: 'payments_count',
                Header: t(lang, "warehouse.operation.report.payments"),
                headerProps: {
                    className: "text-center",
                    colSpan: 2,
                    style: {
                        width: '200px'
                    }
                },
                cellProps: {
                    className: "text-center fw-bold text-dark",
                    style: {
                        width: '60px',
                        minWidth: '60px'
                    }
                },
                Cell: ({row: {original}}) => {
                    return (
                        <h6 className="text-end">{original.contractor_period_report.payments_count}</h6>
                    )
                }
            },
            {
                accessor: 'payments_net_price',
                headerProps: {
                    className: "d-none"
                },
                cellProps: {
                    className: "text-center",
                    style: {
                        minWidth: '140px',
                    }
                },
                Cell: ({row: {original}}) => {
                    if (!original.contractor_period_report.payments_net_price.length) {
                        return (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <SoftBadge bg='primary'
                                           key={nationalCurrency?.id}
                                           className={classNames("w-100")}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(0)}
                                        <small className={'ms-2 fw-bold text-primary'}>{nationalCurrency?.name}</small>
                                    </h6>
                                </SoftBadge>
                            </div>
                        )
                    }

                    return (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            {original.contractor_period_report.payments_net_price.map((netPrice, index) => (
                                <SoftBadge bg='primary'
                                           key={netPrice.currency.id}
                                           className={classNames("w-100", {"mt-2": index !== 0})}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(netPrice.amount)}
                                        <small className={'ms-2 fw-bold text-primary'}>{netPrice.currency.name}</small>
                                    </h6>
                                </SoftBadge>
                            ))}
                        </div>
                    )
                }
            },
            {
                accessor: 'payouts_count',
                Header: t(lang, "warehouse.operation.report.payouts"),
                headerProps: {
                    className: "text-center",
                    colSpan: 2,
                    style: {
                        width: '200px'
                    }
                },
                cellProps: {
                    className: "text-center fw-bold text-dark",
                    style: {
                        width: '60px',
                        minWidth: '60px'
                    }
                },
                Cell: ({row: {original}}) => {
                    return (
                        <h6 className="text-end">{original.contractor_period_report.payouts_count}</h6>
                    )
                }
            },
            {
                accessor: 'payouts_net_price',
                headerProps: {
                    className: "d-none"
                },
                cellProps: {
                    className: "text-center",
                    style: {
                        minWidth: '140px',
                    }
                },
                Cell: ({row: {original}}) => {
                    if (!original.contractor_period_report.payouts_net_price.length) {
                        return (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <SoftBadge bg='primary'
                                           key={nationalCurrency?.id}
                                           className={classNames("w-100")}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(0)}
                                        <small className={'ms-2 fw-bold text-primary'}>{nationalCurrency?.name}</small>
                                    </h6>
                                </SoftBadge>
                            </div>
                        )
                    }

                    return (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            {original.contractor_period_report.payouts_net_price.map((netPrice, index) => (
                                <SoftBadge bg='primary'
                                           key={netPrice.currency.id}
                                           className={classNames("w-100", {"mt-2": index !== 0})}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(netPrice.amount)}
                                        <small className={'ms-2 fw-bold text-primary'}>{netPrice.currency.name}</small>
                                    </h6>
                                </SoftBadge>
                            ))}
                        </div>
                    )
                }
            },
            {
                accessor: 'refunds_count',
                Header: t(lang, "warehouse.operation.report.refunds"),
                headerProps: {
                    className: "text-center",
                    colSpan: 2,
                    style: {
                        width: '200px'
                    }
                },
                cellProps: {
                    className: "text-center fw-bold text-dark",
                    style: {
                        width: '60px',
                        minWidth: '60px'
                    }
                },
                Cell: ({row: {original}}) => {
                    return (
                        <h6 className="text-end">{original.contractor_period_report.refunds_count}</h6>
                    )
                }
            },
            {
                accessor: 'refunds_net_price',
                headerProps: {
                    className: "d-none"
                },
                cellProps: {
                    className: "text-center",
                    style: {
                        minWidth: '140px',
                    }
                },
                Cell: ({row: {original}}) => {
                    if (!original.contractor_period_report.refunds_net_price.length) {
                        return (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <SoftBadge bg='primary'
                                           key={nationalCurrency?.id}
                                           className={classNames("w-100")}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(0)}
                                        <small className={'ms-2 fw-bold text-primary'}>{nationalCurrency?.name}</small>
                                    </h6>
                                </SoftBadge>
                            </div>
                        )
                    }

                    return (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            {original.contractor_period_report.refunds_net_price.map((netPrice, index) => (
                                <SoftBadge bg='primary'
                                           key={netPrice.currency.id}
                                           className={classNames("w-100", {"mt-2": index !== 0})}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(netPrice.amount)}
                                        <small className={'ms-2 fw-bold text-primary'}>{netPrice.currency.name}</small>
                                    </h6>
                                </SoftBadge>
                            ))}
                        </div>
                    )
                }
            },
            {
                accessor: 'purchases_count',
                Header: t(lang, "warehouse.operation.report.purchases"),
                headerProps: {
                    className: "text-center",
                    colSpan: 2,
                    style: {
                        width: '200px'
                    }
                },
                cellProps: {
                    className: "text-center fw-bold text-dark",
                    style: {
                        width: '60px',
                        minWidth: '60px'
                    }
                },
                Cell: ({row: {original}}) => {
                    return (
                        <h6 className="text-end">{original.contractor_period_report.purchases_count}</h6>
                    )
                }
            },
            {
                accessor: 'purchases_net_price',
                headerProps: {
                    className: "d-none"
                },
                cellProps: {
                    className: "text-center",
                    style: {
                        minWidth: '140px',
                    }
                },
                Cell: ({row: {original}}) => {
                    if (!original.contractor_period_report.purchases_net_price.length) {
                        return (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <SoftBadge bg='primary'
                                           key={nationalCurrency?.id}
                                           className={classNames("w-100")}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(0)}
                                        <small className={'ms-2 fw-bold text-primary'}>{nationalCurrency?.name}</small>
                                    </h6>
                                </SoftBadge>
                            </div>
                        )
                    }

                    return (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            {original.contractor_period_report.purchases_net_price.map((netPrice, index) => (
                                <SoftBadge bg='primary'
                                           key={netPrice.currency.id}
                                           className={classNames("w-100", {"mt-2": index !== 0})}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(netPrice.amount)}
                                        <small className={'ms-2 fw-bold text-primary'}>{netPrice.currency.name}</small>
                                    </h6>
                                </SoftBadge>
                            ))}
                        </div>
                    )
                }
            },
            {
                accessor: 'returns_count',
                Header: t(lang, "warehouse.operation.report.returns"),
                headerProps: {
                    className: "text-center",
                    colSpan: 2,
                    style: {
                        width: '200px'
                    }
                },
                cellProps: {
                    className: "text-center fw-bold text-dark",
                    style: {
                        width: '60px',
                        minWidth: '60px'
                    }
                },
                Cell: ({row: {original}}) => {
                    return (
                        <h6 className="text-end">{original.contractor_period_report.returns_count}</h6>
                    )
                }
            },
            {
                accessor: 'returns_net_price',
                headerProps: {
                    className: "d-none",
                },
                cellProps: {
                    className: "text-center",
                    style: {
                        minWidth: '140px',
                        width: '140px'
                    }
                },
                Cell: ({row: {original}}) => {
                    if (!original.contractor_period_report.returns_net_price.length) {
                        return (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <SoftBadge bg='primary'
                                           key={nationalCurrency?.id}
                                           className={classNames("w-100")}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(0)}
                                        <small className={'ms-2 fw-bold text-primary'}>{nationalCurrency?.name}</small>
                                    </h6>
                                </SoftBadge>
                            </div>
                        )
                    }

                    return (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            {original.contractor_period_report.returns_net_price.map((netPrice, index) => (
                                <SoftBadge bg='primary'
                                           key={netPrice.currency.id}
                                           className={classNames("w-100", {"mt-2": index !== 0})}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(netPrice.amount)}
                                        <small className={'ms-2 fw-bold text-primary'}>{netPrice.currency.name}</small>
                                    </h6>
                                </SoftBadge>
                            ))}
                        </div>
                    )
                }
            },
            {
                accessor: 'shortage_count',
                Header: t(lang, "warehouse.operation.item.common.filter.has_shortage_from_order"),
                headerProps: {
                    className: "text-center",
                    colSpan: 2,
                    style: {
                        width: '200px'
                    }
                },
                cellProps: {
                    className: "text-center fw-bold text-dark",
                    style: {
                        width: '60px',
                        minWidth: '60px'
                    }
                },
                Cell: ({row: {original}}) => {
                    return (
                        <h6 className="text-end">{original.contractor_period_report?.sale_order_shortages_count}</h6>
                    )
                }
            },
            {
                accessor: 'shortage_price',
                headerProps: {
                    className: "d-none",
                },
                cellProps: {
                    className: "text-center",
                    style: {
                        minWidth: '140px',
                        width: '140px'
                    }
                },
                Cell: ({row: {original}}) => {

                    if (!original.contractor_period_report.sale_order_shortages_net_price.length) {
                        return (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <SoftBadge bg='primary'
                                           key={nationalCurrency?.id}
                                           className={classNames("w-100")}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(0)}
                                        <small className={'ms-2 fw-bold text-primary'}>{nationalCurrency?.name}</small>
                                    </h6>
                                </SoftBadge>
                            </div>
                        )
                    }

                    return (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            {original.contractor_period_report.sale_order_shortages_net_price.map((shortagePrice, index) => (
                                <ContractorPeriodReportModal contractorReport={original} showShortageColumn={true}>
                                    <SoftBadge bg='warning'
                                               key={shortagePrice.currency.id}
                                               className={classNames("w-100 cursor-pointer text-decoration-underline", {"mt-2": index !== 0})}
                                    >
                                        <h6 className="m-0 text-end">
                                            {numeral.formats[numberFormat].format(shortagePrice.amount)}
                                            <small className={'ms-2 fw-bold text-warning'}>{shortagePrice.currency.name}</small>
                                        </h6>
                                    </SoftBadge>
                                </ContractorPeriodReportModal>
                            ))}
                        </div>
                    )
                }
            },
            {
                accessor: 'telegram_order_count',
                Header: t(lang, "edi.contractor.card.telegram_orders.tab.title"),
                headerProps: {
                    className: "text-center",
                    colSpan: 2,
                    style: {
                        width: '200px'
                    }
                },
                cellProps: {
                    className: "text-center fw-bold text-dark",
                    style: {
                        width: '60px',
                        minWidth: '60px'
                    }
                },
                Cell: ({row: {original}}) => {
                    return (
                        <h6 className="text-end">{original.contractor_period_report?.telegram_order_bot_orders_count}</h6>
                    )
                }
            },
            {
                accessor: 'telegram_order_price',
                headerProps: {
                    className: "d-none",
                },
                cellProps: {
                    className: "text-center",
                    style: {
                        minWidth: '140px',
                        width: '140px'
                    }
                },
                Cell: ({row: {original}}) => {

                    if (!original.contractor_period_report.telegram_order_bot_orders_net_price.length) {
                        return (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <SoftBadge bg='primary'
                                           key={nationalCurrency?.id}
                                           className={classNames("w-100")}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(0)}
                                        <small className={'ms-2 fw-bold text-primary'}>{nationalCurrency?.name}</small>
                                    </h6>
                                </SoftBadge>
                            </div>
                        )
                    }

                    return (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            {original.contractor_period_report.telegram_order_bot_orders_net_price.map((orderPrice, index) => (
                                <SoftBadge bg='success'
                                           key={orderPrice.currency.id}
                                           className={classNames("w-100", {"mt-2": index !== 0})}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(orderPrice.amount)}
                                        <small
                                            className={'ms-2 fw-bold text-success'}>{orderPrice.currency.name}</small>
                                    </h6>
                                </SoftBadge>
                            ))}
                        </div>
                    )
                }
            },
            {
                accessor: 'before_debts',
                Header: t(lang, "warehouse.operation.report.before_debts"),
                headerProps: {
                    className: "text-center",
                },
                cellProps: {
                    className: "text-center fw-bold text-dark",
                },
                Cell: ({row: {original}}) => {
                    if (!original.contractor?.before_debts.length) {
                        return (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <SoftBadge bg='primary'
                                           key={nationalCurrency?.id}
                                           className={classNames("w-100")}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(0)}
                                        <small className={'ms-2 fw-bold text-primary'}>{nationalCurrency?.name}</small>
                                    </h6>
                                </SoftBadge>
                            </div>
                        )
                    }

                    return (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            {original.contractor?.before_debts.map((netPrice, index) => (
                                <SoftBadge bg='danger'
                                           key={netPrice.currency.id}
                                           className={classNames("w-100", {"mt-2": index !== 0})}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(netPrice.amount)}
                                        <small className={'ms-2 fw-bold text-danger'}>{netPrice.currency.name}</small>
                                    </h6>
                                </SoftBadge>
                            ))}
                        </div>
                    )
                }
            },
            {
                accessor: 'debts_net_price',
                Header: t(lang, "warehouse.operation.item.common.real_credit"),
                headerProps: {
                    className: "text-center",
                    colSpan: 2,
                    style: {
                        width: '200px'
                    }
                },
                cellProps: {
                    className: "text-center fw-bold text-dark",
                    style: {
                        width: '60px',
                        minWidth: '60px'
                    }
                },
                Cell: ({row: {original}}) => {
                    if (!original.contractor.debts_net_price.length) {
                        return (
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <SoftBadge bg='primary'
                                           key={nationalCurrency?.id}
                                           className={classNames("w-100")}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(0)}
                                        <small className={'ms-2 fw-bold text-primary'}>{nationalCurrency?.name}</small>
                                    </h6>
                                </SoftBadge>
                            </div>
                        )
                    }

                    return (
                        <div className="d-flex flex-column justify-content-center align-items-center">
                            {original.contractor.debts_net_price.map((netPrice, index) => (
                                <SoftBadge bg='warning'
                                           key={netPrice.currency.id}
                                           className={classNames("w-100", {"mt-2": index !== 0})}
                                >
                                    <h6 className="m-0 text-end">
                                        {numeral.formats[numberFormat].format(netPrice.amount)}
                                        <small className={'ms-2 fw-bold text-warning'}>{netPrice.currency.name}</small>
                                    </h6>
                                </SoftBadge>
                            ))}
                        </div>
                    )
                }
            },
        ];
    }, [lang, nationalCurrency, numberFormat]);

    const columnsData = getActiveColumns(activeColumns, columns)

    const data = useMemo(() => {
        const filteredData = [];
        for (let i = 0; i < contractorReports.length; i++) {
            const contractorReport = contractorReports[i];

            if (filter.name) {
                const searchTermLower = filter.name.trim().toLowerCase();
                const itemNameLower = contractorReport.contractor.name.trim().toLowerCase();
                const transliteratedQuery = transliterate(searchTermLower);
                const transliteratedItemName = transliterate(itemNameLower);
                const filterNameParts = transliteratedQuery.replaceAll('  ', ' ').split(' ');
                if (!(filterNameParts.length === filterNameParts.filter(fnp => transliteratedItemName.indexOf(fnp) > -1).length))
                    continue;
            }

            filteredData.push(contractorReport);
        }

        return filteredData;
    }, [contractorReports, filter]);
    const onDebouncedFilterChanged = useCallback(debounce(setFilter, 500), []);

    return (
        <AdvanceTableWrapper columns={columnsData}
                             data={data}
                             pagination
                             perPage={pagination.pageSize}
                             currentPage={pagination.pageIndex - 1}
        >
            <Card>
                <Card.Header>
                    <Row className={'align-items-end'}>
                        <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                            <Form.Group>
                                <Form.Label>{t(lang, 'warehouse.operation.contractor_report.name')}</Form.Label>
                                <Form.Control name="name"
                                              placeholder={t(lang, 'items.common.datatable.filter.deep_search.placeholder')}
                                              defaultValue={filter.name}
                                              onChange={(e) => onDebouncedFilterChanged({
                                                  ...filter,
                                                  name: e.target.value
                                              })}

                                />
                            </Form.Group>
                        </Col>
                        <Col className={'text-end'}>
                            <IconButton
                                variant={'falcon-primary'}
                                icon={faGear}
                                onClick={() => setShow(true)}
                                title={t(lang, 'items.common.datatable.filter.clear')}
                            />
                            <DataTableSettings
                                show={show}
                                setShow={setShow}
                                allColumns={filteredColumns}
                                changeDataTable={changeContractorPeriodSummaryDataTableSettings}
                            />
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    <AdvanceTable table
                                  headerClassName="bg-200 text-900 text-wrap align-middle"
                                  rowClassName="align-middle white-space-nowrap"
                                  tableProps={{
                                      size: 'sm',
                                      striped: true,
                                      bordered: true,
                                      className: 'fs--1 mb-0 border overflow-hidden',
                                  }}
                    />
                </Card.Body>
                <Card.Footer>
                    <AdvanceTableCustomPagination2 table
                                                   rowsPerPageOptions={[10, 25, 50, 75, 100]}
                                                   onPageChange={(page) => setPagination({
                                                       ...pagination,
                                                       page: page
                                                   })}
                                                   onLimitChange={(limit) => setPagination({
                                                       ...pagination,
                                                       limit: limit
                                                   })}
                    />
                </Card.Footer>
            </Card>
        </AdvanceTableWrapper>
    );
};

export default ContractorsPeriodReportSummaryDataTable;